import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { BookOpenCheck, Download, Loader2 } from 'lucide-react'

interface StickyActionBarProps {
  // Book state
  isTaken: boolean
  hasProgress?: boolean // For "Continue" vs "Read"
  canReadOnline: boolean // EPUB or FB2 (formats supported by FoliateViewer)
  
  // Actions
  onTakeBook: () => void
  onRead: () => void
  onDownload: () => void
  onPreview: () => void
  
  // Loading states
  isTaking?: boolean
  isDownloading?: boolean
  isCheckingAccess?: boolean
  
  // Disabled state (e.g., no level selected)
  disabled?: boolean
  disabledReason?: string
  
  // Show preview (only for epub with original)
  showPreview?: boolean
}

export function StickyActionBar({
  isTaken,
  hasProgress = false,
  canReadOnline,
  onTakeBook,
  onRead,
  onDownload,
  onPreview,
  isTaking = false,
  isDownloading = false,
  isCheckingAccess = false,
  disabled = false,
  disabledReason,
  showPreview = true
}: StickyActionBarProps) {
  const { t } = useTranslation('common')

  // When disabled, show buttons but in disabled state (e.g., no level selected)
  const isButtonsDisabled = disabled || isCheckingAccess

  return (
    <div className="sticky-action-bar fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t z-50">
      {/* Show disabled reason hint above buttons */}
      {disabled && disabledReason && (
        <div className="disabled-hint bg-muted/50 py-1.5 px-3 text-center border-b">
          <p className="text-xs text-muted-foreground">
            {disabledReason}
          </p>
        </div>
      )}
      <div className="max-w-screen-xl mx-auto flex items-center gap-2 p-3">
        {!isTaken ? (
          // NOT TAKEN STATE: Take Book + Preview
          <>
            <Button
              onClick={onTakeBook}
              disabled={isButtonsDisabled || isTaking}
              className="take-book-btn flex-1"
              size="lg"
            >
              {isTaking ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {t('library.taking', 'Taking...')}
                </>
              ) : (
                <>
                  <BookOpenCheck className="mr-2 h-5 w-5" />
                  {t('library.takeBook', 'Take Book')}
                </>
              )}
            </Button>
            {showPreview && (
              <Button
                onClick={onPreview}
                variant="outline"
                className="preview-btn"
                disabled={isButtonsDisabled}
              >
                {t('reader.preview', 'Preview')}
              </Button>
            )}
          </>
        ) : (
          // TAKEN STATE: Read + Download + Preview
          <>
            {canReadOnline && (
              <Button
                onClick={onRead}
                className="read-btn flex-1"
                size="lg"
                disabled={isButtonsDisabled}
              >
                <BookOpenCheck className="mr-2 h-5 w-5" />
                {hasProgress 
                  ? t('reader.continue', 'Continue') 
                  : t('reader.readOnline', 'Read')}
              </Button>
            )}
            <Button
              onClick={onDownload}
              disabled={isButtonsDisabled || isDownloading}
              variant="outline"
              className="download-btn"
              size="icon"
              title={t('common.download')}
            >
              {isDownloading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Download className="h-5 w-5" />
              )}
            </Button>
            {showPreview && (
              <Button
                onClick={onPreview}
                variant="ghost"
                className="preview-btn"
                disabled={isButtonsDisabled}
              >
                {t('reader.preview', 'Preview')}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
